import React, { useEffect, useMemo, useState } from 'react';
import { supabase } from '../lib/supabase';
import { getCurrentUser } from '../lib/auth';
import toast from 'react-hot-toast';
import { Link, useNavigate } from 'react-router-dom';
import {
  FaUsers, FaUserPlus, FaEnvelope, FaCheck, FaTimes, FaArrowLeft,
  FaUserFriends, FaClock, FaComments, FaSearch, FaStar, FaHeart
} from 'react-icons/fa';
import Skeleton from 'react-loading-skeleton';
import { motion, AnimatePresence } from 'framer-motion';

interface User {
  id: string;
  email: string;
  profile_image: string | null;
  full_name: string | null;
  is_online: boolean;
  created_at: string;
  is_featured?: boolean;
  username?: string | null;
}

interface FriendRequest {
  id: string;
  requester_id: string;
  addressee_id: string;
  status: 'pending' | 'accepted' | 'blocked';
  created_at: string;
  requester: User;
}

interface Friend {
  id: string;
  friendship_id: string;
  user: User;
  friendship_created_at: string;
}

export default function Friends() {
  const [friends, setFriends] = useState<Friend[]>([]);
  const [pendingRequests, setPendingRequests] = useState<FriendRequest[]>([]);
  const [sentRequests, setSentRequests] = useState<FriendRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'friends' | 'requests' | 'sent'>('friends');
  const [processing, setProcessing] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [typingAnimation, setTypingAnimation] = useState('');

  const navigate = useNavigate();
  const currentUser = getCurrentUser();

  // Typing animation effect
  useEffect(() => {
    if (searchQuery) {
      const text = searchQuery;
      let index = 0;
      setTypingAnimation('');
      
      const typeInterval = setInterval(() => {
        if (index < text.length) {
          setTypingAnimation(text.slice(0, index + 1));
          index++;
        } else {
          clearInterval(typeInterval);
        }
      }, 50);

      return () => clearInterval(typeInterval);
    } else {
      setTypingAnimation('');
    }
  }, [searchQuery]);

  // Redirect if not logged in (custom auth)
  useEffect(() => {
    if (!currentUser?.id) {
      navigate('/login');
      return;
    }
    loadFriendsData();

    // Realtime updates for friendships involving me
    const ch = supabase
      .channel(`friendships-${currentUser.id}`)
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'user_friendships',
          filter: `or(requester_id.eq.${currentUser.id},addressee_id.eq.${currentUser.id})`
        },
        () => loadFriendsData()
      )
      .subscribe();

    return () => {
      try { supabase.removeChannel(ch); } catch { /* no-op */ }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.id]);

  const filteredFriends = useMemo(() => {
    if (!searchQuery.trim()) return friends;
    const q = searchQuery.toLowerCase();
    return friends.filter(f =>
      f.user.email.toLowerCase().includes(q) ||
      (f.user.full_name && f.user.full_name.toLowerCase().includes(q))
    );
  }, [friends, searchQuery]);

  async function loadFriendsData() {
    if (!currentUser?.id) return;
    setLoading(true);
    try {
      await Promise.all([
        loadFriends(),
        loadPendingRequests(),
        loadSentRequests()
      ]);
    } catch (err: any) {
      console.error('Error loading friends data:', err);
      toast.error(humanizeError(err, 'Failed to load friends data'));
    } finally {
      setLoading(false);
    }
  }

  // Fetch accepted friends without cross-table joins (avoid RLS surprises)
  async function loadFriends() {
    const uid = currentUser!.id;

    const { data: friendships, error } = await supabase
      .from('user_friendships')
      .select('id, created_at, requester_id, addressee_id, status')
      .eq('status', 'accepted')
      .or(`requester_id.eq.${uid},addressee_id.eq.${uid}`)
      .order('created_at', { ascending: false });

    if (error) throw error;

    const friendIds = (friendships || []).map(f =>
      f.requester_id === uid ? f.addressee_id : f.requester_id
    ).filter(Boolean);

    if (friendIds.length === 0) {
      setFriends([]);
      return;
    }

    const { data: users, error: usersErr } = await supabase
      .from('stripeusers')
      .select('id, email, profile_image, full_name, is_online, created_at, is_featured, username')
      .in('id', friendIds);

    if (usersErr) throw usersErr;

    const userMap = new Map(users!.map(u => [u.id, u as User]));
    const formatted: Friend[] = (friendships || []).map(fr => {
      const fid = fr.requester_id === uid ? fr.addressee_id : fr.requester_id;
      const u = userMap.get(fid)!;
      return {
        id: fid,
        friendship_id: fr.id,
        user: u,
        friendship_created_at: fr.created_at
      };
    }).filter(x => !!x.user);

    setFriends(formatted);
  }

  // Incoming (pending) requests to me
  async function loadPendingRequests() {
    const uid = currentUser!.id;

    const { data: reqs, error } = await supabase
      .from('user_friendships')
      .select('id, requester_id, addressee_id, status, created_at')
      .eq('addressee_id', uid)
      .eq('status', 'pending')
      .order('created_at', { ascending: false });

    if (error) throw error;

    if (!reqs || reqs.length === 0) {
      setPendingRequests([]);
      return;
    }

    const requesterIds = Array.from(new Set(reqs.map(r => r.requester_id)));
    const { data: users, error: usersErr } = await supabase
      .from('stripeusers')
      .select('id, email, profile_image, full_name, is_online, created_at')
      .in('id', requesterIds);

    if (usersErr) throw usersErr;

    const userMap = new Map(users!.map(u => [u.id, u as User]));
    const formatted: FriendRequest[] = reqs.map(r => ({
      id: r.id,
      requester_id: r.requester_id,
      addressee_id: r.addressee_id,
      status: r.status as FriendRequest['status'],
      created_at: r.created_at,
      requester: userMap.get(r.requester_id)!
    })).filter(x => !!x.requester);

    setPendingRequests(formatted);
  }

  // Outgoing (pending) requests I sent
  async function loadSentRequests() {
    const uid = currentUser!.id;

    const { data: reqs, error } = await supabase
      .from('user_friendships')
      .select('id, requester_id, addressee_id, status, created_at')
      .eq('requester_id', uid)
      .eq('status', 'pending')
      .order('created_at', { ascending: false });

    if (error) throw error;

    if (!reqs || reqs.length === 0) {
      setSentRequests([]);
      return;
    }

    const addresseeIds = Array.from(new Set(reqs.map(r => r.addressee_id)));
    const { data: users, error: usersErr } = await supabase
      .from('stripeusers')
      .select('id, email, profile_image, full_name, is_online, created_at')
      .in('id', addresseeIds);

    if (usersErr) throw usersErr;

    const userMap = new Map(users!.map(u => [u.id, u as User]));
    const formatted: FriendRequest[] = reqs.map(r => ({
      id: r.id,
      requester_id: r.requester_id,
      addressee_id: r.addressee_id,
      status: r.status as FriendRequest['status'],
      created_at: r.created_at,
      // show the person I'm waiting on (addressee)
      requester: userMap.get(r.addressee_id)!
    })).filter(x => !!x.requester);

    setSentRequests(formatted);
  }

  async function respondToFriendRequest(requestId: string, action: 'accept' | 'decline') {
    try {
      setProcessing(s => new Set(s).add(requestId));

      if (action === 'accept') {
        const { error } = await supabase
          .from('user_friendships')
          .update({ status: 'accepted' })
          .eq('id', requestId);
        if (error) throw error;
        toast.success('Friend request accepted');
      } else {
        const { error } = await supabase
          .from('user_friendships')
          .delete()
          .eq('id', requestId);
        if (error) throw error;
        toast.success('Friend request declined');
      }

      await loadFriendsData();
    } catch (err: any) {
      console.error('Error responding to friend request:', err);
      toast.error(humanizeError(err, 'Failed to respond to friend request'));
    } finally {
      setProcessing(s => {
        const n = new Set(s);
        n.delete(requestId);
        return n;
      });
    }
  }

  async function removeFriend(friendshipId: string, friendName: string) {
    if (!confirm(`Remove ${friendName} from your friends?`)) return;
    try {
      setProcessing(s => new Set(s).add(friendshipId));
      const { error } = await supabase
        .from('user_friendships')
        .delete()
        .eq('id', friendshipId);
      if (error) throw error;
      toast.success('Friend removed');
      await loadFriends();
    } catch (err: any) {
      console.error('Error removing friend:', err);
      toast.error(humanizeError(err, 'Failed to remove friend'));
    } finally {
      setProcessing(s => {
        const n = new Set(s);
        n.delete(friendshipId);
        return n;
      });
    }
  }

  function handleMessage(userId: string) {
    navigate('/messages', { state: { friendId: userId } });
  }

  const formatDate = (dateString: string) =>
    new Date(dateString).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });

  const getTabCount = (tab: 'friends' | 'requests' | 'sent') =>
    tab === 'friends' ? friends.length : tab === 'requests' ? pendingRequests.length : sentRequests.length;

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-black text-white relative overflow-hidden">
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Floating Stars */}
        {[...Array(100)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute bg-yellow-400 rounded-full"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              width: `${Math.random() * 3 + 1}px`,
              height: `${Math.random() * 3 + 1}px`,
            }}
            animate={{
              opacity: [0.3, 1, 0.3],
              scale: [1, 1.5, 1],
            }}
            transition={{
              duration: 2 + Math.random() * 3,
              repeat: Infinity,
              delay: Math.random() * 2,
            }}
          />
        ))}

        {/* Gradient Orbs */}
        <motion.div 
          className="absolute top-20 right-20 w-96 h-96 bg-yellow-400/10 rounded-full blur-3xl"
          animate={{ 
            scale: [1, 1.2, 1],
            opacity: [0.3, 0.6, 0.3]
          }}
          transition={{ 
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
        <motion.div 
          className="absolute bottom-20 left-20 w-80 h-80 bg-white/5 rounded-full blur-3xl"
          animate={{ 
            scale: [1.2, 1, 1.2],
            opacity: [0.2, 0.4, 0.2]
          }}
          transition={{ 
            duration: 10,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 2
          }}
        />

        {/* Floating Hearts */}
        {[...Array(15)].map((_, i) => (
          <motion.div
            key={`heart-${i}`}
            className="absolute text-yellow-400/20"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              fontSize: `${Math.random() * 2 + 1}rem`,
            }}
            animate={{
              y: [0, -20, 0],
              rotate: [0, 360],
              opacity: [0.2, 0.6, 0.2],
            }}
            transition={{
              duration: 8 + Math.random() * 4,
              repeat: Infinity,
              delay: Math.random() * 5,
              ease: "easeInOut"
            }}
          >
            <FaHeart />
          </motion.div>
        ))}
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 relative z-10">
        {/* Header */}
        <motion.div 
          className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-12"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <div className="mb-6 lg:mb-0">
            <Link 
              to="/dashboard" 
              className="inline-flex items-center text-yellow-400 hover:text-yellow-300 mb-6 group transition-all duration-300"
            >
              <motion.div
                className="p-2 rounded-full bg-yellow-400/20 group-hover:bg-yellow-400/30 mr-3"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
              >
                <FaArrowLeft className="w-4 h-4" />
              </motion.div>
              <span className="font-medium">Back to Dashboard</span>
            </Link>
            
            <motion.h1 
              className="text-5xl lg:text-6xl font-bold mb-4"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <span className="bg-gradient-to-r from-yellow-400 via-yellow-300 to-white bg-clip-text text-transparent">
                Friends
              </span>
            </motion.h1>
            
            <motion.p 
              className="text-xl text-gray-300 max-w-2xl"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              Connect with fellow learners and build meaningful friendships
            </motion.p>
          </div>

          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.6 }}
          >
            <Link
              to="/learners"
              className="inline-flex items-center space-x-3 px-8 py-4 bg-gradient-to-r from-yellow-400 to-yellow-500 hover:from-yellow-500 hover:to-yellow-600 text-black rounded-2xl font-bold text-lg transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-yellow-400/25"
            >
              <FaUserPlus className="w-5 h-5" />
              <span>Find New Friends</span>
            </Link>
          </motion.div>
        </motion.div>

        {/* Enhanced Search Bar */}
        <motion.div 
          className="mb-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.8 }}
        >
          <div className="max-w-2xl mx-auto">
            <div className={`relative transition-all duration-500 ${isSearchFocused ? 'transform scale-105' : ''}`}>
              <div className="absolute inset-y-0 left-0 pl-6 flex items-center pointer-events-none">
                <motion.div
                  animate={isSearchFocused ? { 
                    scale: [1, 1.2, 1],
                    rotate: [0, 10, 0]
                  } : {}}
                  transition={{ duration: 0.5 }}
                >
                  <FaSearch className={`w-6 h-6 transition-colors duration-300 ${
                    isSearchFocused ? 'text-yellow-400' : 'text-gray-400'
                  }`} />
                </motion.div>
              </div>
              
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onFocus={() => setIsSearchFocused(true)}
                onBlur={() => setIsSearchFocused(false)}
                placeholder="Search friends by name or email..."
                className="w-full pl-16 pr-6 py-5 bg-white/10 backdrop-blur-xl border-2 border-white/20 rounded-2xl text-white placeholder-white/50 focus:outline-none focus:ring-4 focus:ring-yellow-400/30 focus:border-yellow-400/50 transition-all duration-300 text-lg font-medium"
              />
              
              {/* Typing Animation Overlay */}
              {typingAnimation && (
                <div className="absolute inset-y-0 left-16 flex items-center pointer-events-none">
                  <span className="text-yellow-400 font-medium text-lg">
                    {typingAnimation}
                    <motion.span
                      className="inline-block w-0.5 h-6 bg-yellow-400 ml-1"
                      animate={{ opacity: [1, 0] }}
                      transition={{ duration: 0.8, repeat: Infinity }}
                    />
                  </span>
                </div>
              )}

              {/* Search Results Count */}
              {searchQuery && (
                <motion.div
                  className="absolute inset-y-0 right-6 flex items-center"
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.3 }}
                >
                  <span className="text-sm text-yellow-400 bg-yellow-400/20 px-3 py-1 rounded-full">
                    {filteredFriends.length} found
                  </span>
                </motion.div>
              )}

              {/* Glowing Border Effect */}
              <div className={`absolute inset-0 rounded-2xl transition-all duration-300 pointer-events-none ${
                isSearchFocused 
                  ? 'bg-gradient-to-r from-yellow-400/20 via-transparent to-yellow-400/20 animate-pulse' 
                  : ''
              }`} />
            </div>
          </div>
        </motion.div>

        {/* Tabs */}
        <motion.div 
          className="bg-white/10 backdrop-blur-xl rounded-3xl shadow-2xl mb-8 border border-white/20"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1 }}
        >
          <div className="p-2">
            <nav className="flex space-x-2">
              {(['friends','requests','sent'] as const).map((tab) => (
                <motion.button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`flex-1 py-4 px-6 rounded-2xl font-bold text-sm transition-all duration-300 relative overflow-hidden ${
                    activeTab === tab
                      ? 'bg-gradient-to-r from-yellow-400 to-yellow-500 text-black shadow-lg'
                      : 'text-white hover:bg-white/10'
                  }`}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  {/* Animated Background for Active Tab */}
                  {activeTab === tab && (
                    <motion.div
                      className="absolute inset-0 bg-gradient-to-r from-yellow-300 to-yellow-600"
                      layoutId="activeTab"
                      transition={{ type: "spring", stiffness: 300, damping: 30 }}
                    />
                  )}
                  
                  <div className="relative z-10 flex items-center justify-center space-x-2">
                    {tab === 'friends' && <FaUserFriends className="w-4 h-4" />}
                    {tab === 'requests' && <FaClock className="w-4 h-4" />}
                    {tab === 'sent' && <FaUserPlus className="w-4 h-4" />}
                    <span>
                      {tab === 'friends' ? 'My Friends' : tab === 'requests' ? 'Requests' : 'Sent'}
                    </span>
                    {getTabCount(tab) > 0 && (
                      <motion.span
                        className={`px-2 py-1 rounded-full text-xs font-bold ${
                          activeTab === tab
                            ? 'bg-black/20 text-black'
                            : tab === 'requests'
                            ? 'bg-red-500/20 text-red-400'
                            : 'bg-yellow-400/20 text-yellow-400'
                        }`}
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ type: "spring", stiffness: 300, damping: 20 }}
                      >
                        {getTabCount(tab)}
                      </motion.span>
                    )}
                  </div>
                </motion.button>
              ))}
            </nav>
          </div>
        </motion.div>

        {/* Tab Content */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.5 }}
          >
            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {Array(6).fill(0).map((_, i) => (
                  <div key={i} className="bg-white/10 backdrop-blur-xl rounded-2xl p-6 border border-white/20">
                    <div className="flex items-center space-x-4 mb-4">
                      <Skeleton circle width={60} height={60} baseColor="#374151" highlightColor="#4B5563" />
                      <div className="flex-1">
                        <Skeleton height={20} width="60%" baseColor="#374151" highlightColor="#4B5563" />
                        <Skeleton height={16} width="40%" baseColor="#374151" highlightColor="#4B5563" />
                      </div>
                    </div>
                    <Skeleton width={80} height={32} baseColor="#374151" highlightColor="#4B5563" />
                  </div>
                ))}
              </div>
            ) : (
              <>
                {/* Friends Tab */}
                {activeTab === 'friends' && (
                  <div>
                    {filteredFriends.length > 0 ? (
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {filteredFriends.map((friend, index) => (
                          <motion.div
                            key={friend.id}
                            className="group bg-white/10 backdrop-blur-xl rounded-3xl p-8 border border-white/20 hover:border-yellow-400/50 transition-all duration-300 hover:bg-white/15 relative overflow-hidden text-center"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: index * 0.1 }}
                            whileHover={{ scale: 1.02, y: -5 }}
                          >
                            {/* Hover Glow Effect */}
                            <div className="absolute inset-0 bg-gradient-to-br from-yellow-400/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-2xl" />
                            
                            <div className="relative z-10">
                              {/* Featured Badge */}
                              {friend.user.is_featured && (
                                <div className="mb-4">
                                  <span className="inline-block px-4 py-2 bg-gradient-to-r from-yellow-400 to-yellow-500 text-black rounded-full text-sm font-bold shadow-lg">
                                    Featured
                                  </span>
                                </div>
                              )}

                              {/* Profile Image */}
                              <div className="mb-6">
                                <div className="relative">
                                  <motion.div 
                                    className="w-24 h-24 mx-auto rounded-full overflow-hidden border-4 border-yellow-400/50 shadow-lg"
                                    whileHover={{ scale: 1.1 }}
                                    transition={{ duration: 0.3 }}
                                  >
                                    {friend.user.profile_image ? (
                                      <img src={friend.user.profile_image} alt="Profile" className="w-full h-full object-cover" />
                                    ) : (
                                      <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-yellow-400 to-yellow-600 text-black font-bold text-2xl">
                                        {friend.user.email.charAt(0).toUpperCase()}
                                      </div>
                                    )}
                                  </motion.div>
                                  
                                  {/* Online Status */}
                                  <motion.div
                                    className={`absolute -bottom-1 -right-1 w-6 h-6 rounded-full border-3 border-black shadow-lg ${
                                      friend.user.is_online ? 'bg-green-400' : 'bg-gray-500'
                                    }`}
                                    animate={friend.user.is_online ? {
                                      scale: [1, 1.2, 1],
                                      boxShadow: ['0 0 0 0 rgba(34, 197, 94, 0.7)', '0 0 0 10px rgba(34, 197, 94, 0)', '0 0 0 0 rgba(34, 197, 94, 0)']
                                    } : {}}
                                    transition={{ duration: 2, repeat: Infinity }}
                                  />
                                </div>
                              </div>

                              {/* User Name */}
                              <div className="mb-6">
                                {friend.user.username || friend.user.full_name ? (
                                  <h3 className="text-2xl font-bold text-white mb-2 group-hover:text-yellow-300 transition-colors duration-300">
                                    {friend.user.username || friend.user.full_name}
                                  </h3>
                                ) : (
                                  <div className="mb-8"></div>
                                )}
                                
                                <div className="flex items-center justify-center space-x-2">
                                  <div className={`w-2 h-2 rounded-full ${friend.user.is_online ? 'bg-green-400' : 'bg-gray-500'}`} />
                                  <p className="text-sm text-gray-400">
                                    {friend.user.is_online ? 'Online' : 'Offline'}
                                  </p>
                                </div>
                              </div>

                              {/* Action Buttons */}
                              <div className="flex items-center space-x-3">
                                <motion.button
                                  onClick={() => handleMessage(friend.user.id)}
                                  className="flex-1 flex items-center justify-center space-x-2 px-6 py-4 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white rounded-2xl font-bold transition-all duration-300 shadow-lg text-lg"
                                  whileHover={{ scale: 1.05 }}
                                  whileTap={{ scale: 0.95 }}
                                >
                                  <FaComments className="w-4 h-4" />
                                  <span>Message</span>
                                </motion.button>
                                
                                <motion.button
                                  onClick={() => removeFriend(friend.friendship_id, friend.user.full_name || friend.user.email)}
                                  disabled={processing.has(friend.friendship_id)}
                                  className="p-4 bg-red-500/20 hover:bg-red-500/30 disabled:opacity-50 rounded-xl text-red-400 hover:text-red-300 transition-all duration-300 border border-red-500/30"
                                  whileHover={{ scale: 1.1 }}
                                  whileTap={{ scale: 0.9 }}
                                >
                                  <FaTimes className="w-5 h-5" />
                                </motion.button>
                              </div>
                            </div>
                          </motion.div>
                        ))}
                      </div>
                    ) : (
                      <motion.div 
                        className="text-center py-20"
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.8 }}
                      >
                        <motion.div
                          className="w-32 h-32 bg-gradient-to-br from-yellow-400/20 to-yellow-600/20 rounded-full flex items-center justify-center mx-auto mb-8 border border-yellow-400/30"
                          animate={{
                            scale: [1, 1.05, 1],
                            rotate: [0, 5, -5, 0]
                          }}
                          transition={{ duration: 4, repeat: Infinity }}
                        >
                          <FaUserFriends className="w-16 h-16 text-yellow-400" />
                        </motion.div>
                        
                        <h3 className="text-3xl font-bold text-white mb-4">
                          {searchQuery ? 'No friends found' : 'No friends yet'}
                        </h3>
                        <p className="text-gray-400 mb-8 text-lg max-w-md mx-auto">
                          {searchQuery ? `No friends match "${searchQuery}"` : 'Start connecting with other learners and build your network!'}
                        </p>
                        
                        {!searchQuery && (
                          <motion.div
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                          >
                            <Link
                              to="/learners"
                              className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-yellow-400 to-yellow-500 text-black rounded-2xl font-bold text-lg hover:from-yellow-500 hover:to-yellow-600 transition-all duration-300 shadow-lg"
                            >
                              <FaUserPlus className="w-5 h-5 mr-3" />
                              Find Friends
                            </Link>
                          </motion.div>
                        )}
                      </motion.div>
                    )}
                  </div>
                )}

                {/* Pending Requests Tab */}
                {activeTab === 'requests' && (
                  <div>
                    {pendingRequests.length > 0 ? (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {pendingRequests.map((request, index) => (
                          <motion.div
                            key={request.id}
                            className="bg-gradient-to-br from-yellow-400/20 to-yellow-600/20 backdrop-blur-xl rounded-2xl p-6 border border-yellow-400/30 relative overflow-hidden"
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.5, delay: index * 0.1 }}
                          >
                            {/* Animated Border */}
                            <div className="absolute inset-0 bg-gradient-to-r from-yellow-400/30 via-transparent to-yellow-400/30 animate-pulse rounded-2xl" />
                            
                            <div className="relative z-10">
                              <div className="flex items-center space-x-4 mb-6">
                                <motion.div 
                                  className="w-16 h-16 rounded-full overflow-hidden border-3 border-yellow-400 shadow-lg"
                                  whileHover={{ scale: 1.1, rotate: 5 }}
                                >
                                  {request.requester.profile_image ? (
                                    <img src={request.requester.profile_image} alt="Profile" className="w-full h-full object-cover" />
                                  ) : (
                                    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-yellow-400 to-yellow-600 text-black font-bold text-xl">
                                      {request.requester.email.charAt(0).toUpperCase()}
                                    </div>
                                  )}
                                </motion.div>
                                
                                <div className="flex-1 min-w-0">
                                  <h3 className="text-lg font-bold text-white truncate">
                                    {request.requester.full_name || request.requester.email}
                                  </h3>
                                  <p className="text-sm text-gray-300 truncate">{request.requester.email}</p>
                                  <p className="text-xs text-yellow-400 mt-1">Sent {formatDate(request.created_at)}</p>
                                </div>
                              </div>
                              
                              <div className="flex items-center space-x-3">
                                <motion.button
                                  onClick={() => respondToFriendRequest(request.id, 'accept')}
                                  disabled={processing.has(request.id)}
                                  className="flex-1 flex items-center justify-center space-x-2 px-4 py-3 bg-green-500 hover:bg-green-600 disabled:opacity-50 rounded-xl text-white font-bold transition-all duration-300 shadow-lg"
                                  whileHover={{ scale: 1.05 }}
                                  whileTap={{ scale: 0.95 }}
                                >
                                  <FaCheck className="w-4 h-4" />
                                  <span>Accept</span>
                                </motion.button>
                                
                                <motion.button
                                  onClick={() => respondToFriendRequest(request.id, 'decline')}
                                  disabled={processing.has(request.id)}
                                  className="flex-1 flex items-center justify-center space-x-2 px-4 py-3 bg-red-500 hover:bg-red-600 disabled:opacity-50 rounded-xl text-white font-bold transition-all duration-300 shadow-lg"
                                  whileHover={{ scale: 1.05 }}
                                  whileTap={{ scale: 0.95 }}
                                >
                                  <FaTimes className="w-4 h-4" />
                                  <span>Decline</span>
                                </motion.button>
                              </div>
                            </div>
                          </motion.div>
                        ))}
                      </div>
                    ) : (
                      <motion.div 
                        className="text-center py-20"
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.8 }}
                      >
                        <motion.div
                          className="w-32 h-32 bg-gradient-to-br from-yellow-400/20 to-yellow-600/20 rounded-full flex items-center justify-center mx-auto mb-8 border border-yellow-400/30"
                          animate={{
                            scale: [1, 1.05, 1],
                            rotate: [0, 5, -5, 0]
                          }}
                          transition={{ duration: 4, repeat: Infinity }}
                        >
                          <FaClock className="w-16 h-16 text-yellow-400" />
                        </motion.div>
                        
                        <h3 className="text-3xl font-bold text-white mb-4">No pending requests</h3>
                        <p className="text-gray-400 text-lg">You don't have any pending friend requests</p>
                      </motion.div>
                    )}
                  </div>
                )}

                {/* Sent Requests Tab */}
                {activeTab === 'sent' && (
                  <div>
                    {sentRequests.length > 0 ? (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {sentRequests.map((request, index) => (
                          <motion.div
                            key={request.id}
                            className="bg-gradient-to-br from-blue-500/20 to-blue-700/20 backdrop-blur-xl rounded-2xl p-6 border border-blue-400/30 relative overflow-hidden"
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.5, delay: index * 0.1 }}
                          >
                            <div className="relative z-10">
                              <div className="flex items-center space-x-4 mb-6">
                                <motion.div 
                                  className="w-16 h-16 rounded-full overflow-hidden border-3 border-blue-400 shadow-lg"
                                  whileHover={{ scale: 1.1, rotate: -5 }}
                                >
                                  {request.requester.profile_image ? (
                                    <img src={request.requester.profile_image} alt="Profile" className="w-full h-full object-cover" />
                                  ) : (
                                    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-blue-400 to-blue-600 text-white font-bold text-xl">
                                      {request.requester.email.charAt(0).toUpperCase()}
                                    </div>
                                  )}
                                </motion.div>
                                
                                <div className="flex-1 min-w-0">
                                  <h3 className="text-lg font-bold text-white truncate">
                                    {request.requester.full_name || request.requester.email}
                                  </h3>
                                  <p className="text-sm text-gray-300 truncate">{request.requester.email}</p>
                                  <p className="text-xs text-blue-400 mt-1">Sent {formatDate(request.created_at)}</p>
                                </div>
                              </div>
                              
                              <div className="flex items-center space-x-3">
                                <div className="flex-1 flex items-center justify-center space-x-2 px-4 py-3 bg-yellow-400/20 text-yellow-300 rounded-xl font-bold border border-yellow-400/30">
                                  <motion.div
                                    animate={{ rotate: 360 }}
                                    transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                                  >
                                    <FaClock className="w-4 h-4" />
                                  </motion.div>
                                  <span>Pending</span>
                                </div>
                                
                                <motion.button
                                  onClick={() => {/* Cancel request logic */}}
                                  disabled={processing.has(request.id)}
                                  className="p-3 bg-gray-500/20 hover:bg-gray-500/30 disabled:opacity-50 rounded-xl text-gray-400 hover:text-gray-300 transition-all duration-300 border border-gray-500/30"
                                  whileHover={{ scale: 1.1 }}
                                  whileTap={{ scale: 0.9 }}
                                >
                                  <FaTimes className="w-4 h-4" />
                                </motion.button>
                              </div>
                            </div>
                          </motion.div>
                        ))}
                      </div>
                    ) : (
                      <motion.div 
                        className="text-center py-20"
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.8 }}
                      >
                        <motion.div
                          className="w-32 h-32 bg-gradient-to-br from-yellow-400/20 to-yellow-600/20 rounded-full flex items-center justify-center mx-auto mb-8 border border-yellow-400/30"
                          animate={{
                            scale: [1, 1.05, 1],
                            rotate: [0, 5, -5, 0]
                          }}
                          transition={{ duration: 4, repeat: Infinity }}
                        >
                          <FaUserPlus className="w-16 h-16 text-yellow-400" />
                        </motion.div>
                        
                        <h3 className="text-3xl font-bold text-white mb-4">No sent requests</h3>
                        <p className="text-gray-400 mb-8 text-lg">You haven't sent any friend requests yet</p>
                        
                        <motion.div
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                        >
                          <Link
                            to="/learners"
                            className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-yellow-400 to-yellow-500 text-black rounded-2xl font-bold text-lg hover:from-yellow-500 hover:to-yellow-600 transition-all duration-300 shadow-lg"
                          >
                            <FaUserPlus className="w-5 h-5 mr-3" />
                            Find People to Connect
                          </Link>
                        </motion.div>
                      </motion.div>
                    )}
                  </div>
                )}
              </>
            )}
          </motion.div>
        </AnimatePresence>

        {/* Enhanced Stats Cards */}
        <motion.div 
          className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.2 }}
        >
          {[
            { 
              icon: FaUserFriends, 
              label: 'Total Friends', 
              value: friends.length, 
              color: 'from-green-400 to-green-600',
              bgColor: 'from-green-500/20 to-green-700/20',
              borderColor: 'border-green-400/30'
            },
            { 
              icon: FaClock, 
              label: 'Pending Requests', 
              value: pendingRequests.length, 
              color: 'from-yellow-400 to-yellow-600',
              bgColor: 'from-yellow-500/20 to-yellow-700/20',
              borderColor: 'border-yellow-400/30'
            },
            { 
              icon: FaUserPlus, 
              label: 'Sent Requests', 
              value: sentRequests.length, 
              color: 'from-blue-400 to-blue-600',
              bgColor: 'from-blue-500/20 to-blue-700/20',
              borderColor: 'border-blue-400/30'
            }
          ].map((stat, index) => (
            <motion.div
              key={stat.label}
              className={`bg-gradient-to-br ${stat.bgColor} backdrop-blur-xl rounded-2xl p-6 border ${stat.borderColor} relative overflow-hidden group hover:scale-105 transition-all duration-300`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 1.4 + index * 0.1 }}
              whileHover={{ y: -5 }}
            >
              {/* Animated Background Glow */}
              <div className={`absolute inset-0 bg-gradient-to-br ${stat.color} opacity-0 group-hover:opacity-10 transition-opacity duration-300 rounded-2xl`} />
              
              <div className="relative z-10 flex items-center">
                <motion.div 
                  className={`w-16 h-16 bg-gradient-to-br ${stat.color} rounded-2xl flex items-center justify-center shadow-lg mr-6`}
                  whileHover={{ 
                    scale: 1.1,
                    rotate: [0, -5, 5, 0]
                  }}
                  transition={{ duration: 0.5 }}
                >
                  <stat.icon className="w-8 h-8 text-white" />
                </motion.div>
                
                <div>
                  <p className="text-sm font-medium text-gray-300 mb-1">{stat.label}</p>
                  <motion.p 
                    className="text-4xl font-bold text-white"
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ 
                      type: "spring", 
                      stiffness: 200, 
                      delay: 1.6 + index * 0.1 
                    }}
                  >
                    {stat.value}
                  </motion.p>
                </div>
              </div>

              {/* Floating Particles */}
              {[...Array(5)].map((_, i) => (
                <motion.div
                  key={i}
                  className={`absolute w-1 h-1 bg-gradient-to-r ${stat.color} rounded-full`}
                  style={{
                    left: `${Math.random() * 100}%`,
                    top: `${Math.random() * 100}%`,
                  }}
                  animate={{
                    y: [0, -20, 0],
                    opacity: [0.3, 1, 0.3],
                    scale: [1, 1.5, 1]
                  }}
                  transition={{
                    duration: 3 + Math.random() * 2,
                    repeat: Infinity,
                    delay: Math.random() * 2,
                    ease: "easeInOut"
                  }}
                />
              ))}
            </motion.div>
          ))}
        </motion.div>
      </div>

      {/* Floating Action Button for Mobile */}
      <motion.div 
        className="fixed bottom-8 right-8 lg:hidden z-50"
        initial={{ scale: 0, rotate: -180 }}
        animate={{ scale: 1, rotate: 0 }}
        transition={{ 
          type: "spring", 
          stiffness: 200, 
          delay: 2 
        }}
      >
        <Link
          to="/learners"
          className="w-16 h-16 bg-gradient-to-r from-yellow-400 to-yellow-500 rounded-full flex items-center justify-center shadow-2xl hover:shadow-yellow-400/50 transition-all duration-300"
        >
          <motion.div
            whileHover={{ scale: 1.2, rotate: 360 }}
            transition={{ duration: 0.5 }}
          >
            <FaUserFriends className="w-8 h-8 text-black" />
          </motion.div>
        </Link>
      </motion.div>

      {/* Custom Styles */}
      <style jsx>{`
        @keyframes shimmer {
          0% {
            background-position: -200% 0;
          }
          100% {
            background-position: 200% 0;
          }
        }
        
        .shimmer-text {
          background: linear-gradient(90deg, 
            rgba(255, 255, 255, 0.8) 0%, 
            rgba(250, 204, 21, 1) 25%, 
            rgba(255, 255, 255, 0.8) 50%, 
            rgba(250, 204, 21, 1) 75%, 
            rgba(255, 255, 255, 0.8) 100%);
          background-size: 200% auto;
          color: transparent;
          background-clip: text;
          -webkit-background-clip: text;
          animation: shimmer 3s linear infinite;
        }
      `}</style>
    </div>
  );
}

/** Friendly error text for common Supabase/RLS issues */
function humanizeError(err: any, fallback: string) {
  const msg = String(err?.message || '');
  if (msg.toLowerCase().includes('row level security') || err?.code === 'PGRST301') {
    return 'Not authorized by RLS. Ensure a valid Supabase auth session and policies allow select on both tables.';
  }
  if (err?.code === 'PGRST116') return fallback; // no rows
  return fallback;
}